// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ICreateUser, IUser, IUserSubscriptionDetail, IUserSubscriptions, SubscriptionPaymentSource, UserRole} from 'src/types/user';
import {BreakpointObserver, Breakpoints, BreakpointState} from '@angular/cdk/layout';
import {BehaviorSubject, EMPTY, forkJoin, Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap} from 'rxjs/operators';
import AuthHelper from 'src/app/auth/authHelper';
import {HeaderService} from 'src/app/header.service';
import {UserService} from 'src/services/user-service';
import {NotificationService} from 'src/services/notification-service';
import {D3eService} from 'src/services/d3e-service';
import {AddOrganizationComponent, PlanChoiceValue} from 'src/app/dialogs/add-organization/add-organization.component';
import {EditOrgComponent} from 'src/app/dialogs/edit-org/edit-org.component';
import {EditLicenseCountComponent} from 'src/app/dialogs/edit-license-count/edit-license-count.component';
import {EditExpDateComponent} from 'src/app/dialogs/edit-exp-date/edit-exp-date.component';
import {AlertDialogComponent} from 'src/app/dialogs/alert/alert.component';
import {SelectionModel} from '@angular/cdk/collections';
import {TranslateService} from '@ngx-translate/core';
import {AddChildSubscriptionComponent} from '../../dialogs/add-child-subscription/add-child-subscription.component';
import {LicenseType, StripeProductTier} from '../../../types/subscription';
import {IGroup, OrgType} from '../../../types/group';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IPartner, Partners} from '../../../types/partners';
import authHelper from '../../auth/authHelper';
import {loadOrganizations} from '../../store/organizations/organizations.actions';
import {AppState} from '../../store/app.state';
import {Store} from '@ngrx/store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

enum ExpirationFilter {
  ALL_SUBSCRIPTIONS,
  LESS_THAN_1_MONTH,
  LESS_THAN_3_MONTH,
  LESS_THAN_6_MONTH
}

type PartnerMap = {[key: string]: IPartner};

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-organizations',
    templateUrl: './organizations.component.html',
    styleUrls: ['./organizations.component.scss'],
    standalone: false
})
export class OrganizationsComponent implements OnInit  {
  readonly subtype = LicenseType;

  partnersById$: Observable<PartnerMap>;
  partnersById: PartnerMap;

  constructor(public dialog: MatDialog,
    private headerService: HeaderService,
    private userService: UserService,
    private d3eService: D3eService,
    private notification: NotificationService,
    public translate: TranslateService,
    private readonly store: Store<AppState>,
    public breakpointObserver: BreakpointObserver) {
      this.translate.get(['TITLE_ORG', 'ORG_AVAILABLE', 'ORG_ADD', 'ORG_REACTIVATE', 'ORG_DEACTIVATE', 'ORG_ORG', 'ORG_EMAIL',
        'ORG_USER', 'FIRST', 'LAST', 'ORG_SUB', 'ORG_STATUS', 'ORG_ALLOCATED', 'ORG_USED', 'ORG_LAST', 'ORG_EXPIRES',
        'ORG_ADD_SUBSCRIPTION', 'DEACTIVATE_ORGS_TITLE', 'DEACTIVATE_ORGS_CONTENT', 'CANCEL', 'CONFIRM', 'D3E_PREMIUM', 'D3E_PERSONAL',
        'D3E_FREE', 'REACTIVATE_ORGS_TITLE', 'REACTIVATE_ORGS_CONTENT', 'REACTIVATE_ORGS_CONTENT_ADD', 'ORGANIZATIONS_REACTIVATED',
        'ERROR_OCCURRED_UNKNOWN', 'ERROR_UPDATING_SUB_COUNT', 'SUB_COUNT_UPDATED', 'UNLIMITED', 'EXPIRED', 'ORG_UPDATED',
        'NEW_ORG_CREATED', 'LICENSE_COUNT_UPDATED', 'EXPIRATION_UPDATED', 'ORGS_DEACTIVATED', 'ACTIVE', 'INACTIVE',
        'L_TABLE_SEARCH', 'L_SEARCH_PHOLDER', 'ADO_SYS_DEFAULT']).subscribe(vals => {
        this.fAvailable = vals['ORG_AVAILABLE'];
        this.fAdd = vals['ORG_ADD'];
        this.fReactivate = vals['ORG_REACTIVATE'];
        this.fDeactivate = vals['ORG_DEACTIVATE'];
        this.fOrg = vals['ORG_ORG'];
        this.fEmail = vals['ORG_EMAIL'];
        this.fUser = vals['ORG_USER'];
        this.fFName = vals['FIRST'];
        this.fLName = vals['LAST'];
        this.fSub = vals['ORG_SUB'];
        this.fStatus = vals['ORG_STATUS'];
        this.fAllocated = vals['ORG_ALLOCATED'];
        this.fUsed = vals['ORG_USED'];
        this.fLast = vals['ORG_LAST'];
        this.fExpires = vals['ORG_EXPIRES'];
        this.fAddSubscription = vals['ORG_ADD_SUBSCRIPTION'];
        this.fDeactivateOrgsTitle = vals['DEACTIVATE_ORGS_TITLE'];
        this.fDeactivateOrgsContent = vals['DEACTIVATE_ORGS_CONTENT'];
        this.fCancel = vals['CANCEL'];
        this.fConfirm = vals['CONFIRM'];
        this.fReactivateOrgsTitle = vals['REACTIVATE_ORGS_TITLE'];
        this.fReactivateOrgsContent = vals['REACTIVATE_ORGS_CONTENT'];
        this.fReactivateOrgsContentAdd = vals['REACTIVATE_ORGS_CONTENT_ADD'];
        this.fUnknownError = vals['ERROR_OCCURRED_UNKNOWN'];
        this.fOrgsReactivated = vals['ORGANIZATIONS_REACTIVATED'];
        this.fD3EPremium = vals['D3E_PREMIUM'];
        this.fD3EPersonal = vals['D3E_PERSONAL'];
        this.fD3EFree = vals['D3E_FREE'];
        this.fSubCountUpdated = vals['SUB_COUNT_UPDATED'];
        this.fErrorUpdatingSubCount = vals['ERROR_UPDATING_SUB_COUNT'];
        this.fUnlimited = vals['UNLIMITED'];
        this.fExpired = vals['EXPIRED'];
        this.fNewOrgCreated = vals['NEW_ORG_CREATED'];
        this.fOrgUpdated = vals['ORG_UPDATED'];
        this.fLicenseCountUpdated = vals['LICENSE_COUNT_UPDATED'];
        this.fExpirationUpdated = vals['EXPIRATION_UPDATED'];
        this.fOrgsDeactivated = vals['ORGS_DEACTIVATED'];
        this.fActive = vals['ACTIVE'];
        this.fInactive = vals['INACTIVE'];
        this.fTableSearch = vals['L_TABLE_SEARCH'];
        this.fTableHolder = vals['L_SEARCH_PHOLDER'];
        this.fAdoSysDefault = vals['ADO_SYS_DEFAULT'];

        this.title = vals['TITLE_ORG'];
      });
      this.headerService.setTitle(this.title);
      this.headerService.setSubtitle('');
      this.headerService.setIcon('mdi-domain');
      this.headerService.showUserFilter(false);
  }
  public readonly PaymentSource = SubscriptionPaymentSource;
  private currentUser: string;
  currentUserId: number;
  currentGroupId: number;
  organizationsCount = 0;
  subscriptionCount = 0;
  allOrgsCount = 0;
  allSubsCount = 0;
  allUsedLicenses = 0;
  totalLicenses: number | null = 0;
  allocatedLicensesDisplay = '0';
  allAllocatedLicensesDisplay = '0';
  usedLicenses = 0;
  cardCols = 5;
  ExpirationFilterType = ExpirationFilter;
  expirationFilter: ExpirationFilter =  ExpirationFilter.LESS_THAN_1_MONTH;

  managing_user: IUserSubscriptions;
  managing_user_select_licenses = 0;
  managing_user_plus_licenses = 0;
  managing_user_hardware_licenses = 0;
  managing_user_hardware2_licenses = 0;
  managing_user_bitlocker_licenses = 0;
  managing_user_fde_licenses = 0;
  managing_user_agent_licenses = 0;
  managing_user_fallback_licenses = 0;
  managing_user_total_licenses = 0;
  managing_user_select_sub_id = 0;
  managing_user_plus_sub_id = 0;
  managing_user_hardware_sub_id = 0;
  managing_user_hardware2_sub_id = 0;
  managing_user_bitlocker_sub_id = 0;
  managing_user_fde_sub_id = 0;
  managing_user_agent_sub_id = 0;
  managing_user_fallback_sub_id = 0;

  matTableUserDataSource: any;
  loadingUsers$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  usersColumns: string[] = [];

  currentOrg: IGroup;

  userPageIndex = 0;
  selection = new SelectionModel<IUser>(true, []);
  disable_reactivate_btn = true;
  selected = {};

  disable_deactivate_btn = true;
  disable_add_subscription_btn = true;
  disable_priority_lock_btn = true;
  disable_priority_lock_release_btn = true;
  active_orgs_selected = 0;
  deactivated_orgs_selected = 0;

  priority_lock_engaged_selected = 0;
  priority_lock_released_selected = 0;

  // Any date at or past this is considered open forever.
  openEndedDate = new Date(2050, 0, 1);
  tomorrow = dayjs.utc().add(1, 'day').startOf('day').toDate();
  yesterday = dayjs.utc().subtract(1, 'day').startOf('day').toDate();
  lastMonth = dayjs.utc().subtract(1, 'month').startOf('day').toDate();
  defaultDate = dayjs.utc().add(1, 'year').endOf('day').toDate();
  maxDurationDays = null;
  infiniteLicenses = 1000000;
  choosePlan = true;
  searchTerm = '';
  showOnlyActive: boolean = true;

  pageSize = 100;
  pageSizeOptions: number[] = [5, 10, 25, 100, 1000];

  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    if (this.matTableUserDataSource) {
      this.matTableUserDataSource.paginator = paginator;
    }
  }
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (this.matTableUserDataSource) {
      this.matTableUserDataSource.sort = sort;
    }
  }
  fAvailable: string;
  fAdd: string;
  fReactivate: string;
  fDeactivate: string;
  fOrg: string;
  fEmail: string;
  fUser: string;
  fFName: string;
  fLName: string;
  fSub: string;
  fStatus: string;
  fAllocated: string;
  fUsed: string;
  fLast: string;
  fExpires: string;
  fAddSubscription: string;
  fDeactivateOrgsTitle: string;
  fDeactivateOrgsContent: string;
  fCancel: string;
  fConfirm: string;
  fReactivateOrgsTitle: string;
  fReactivateOrgsContent: string;
  fReactivateOrgsContentAdd: string;
  fUnknownError: string;
  fOrgsReactivated: string;
  fD3EPremium: string;
  fD3EPersonal: string;
  fD3EFree: string;
  fSubCountUpdated: string;
  fErrorUpdatingSubCount: string;
  fUnlimited: string;
  fExpired: string;
  fNewOrgCreated: string;
  fOrgUpdated: string;
  fLicenseCountUpdated: string;
  fExpirationUpdated: string;
  fOrgsDeactivated: string;
  fActive: string;
  fInactive: string;
  fTableSearch: string;
  fTableHolder: string;
  fAdoSysDefault: string;
  title: string;
  LicenseType: any;

  ngOnInit() {
    this.currentUser = AuthHelper.getUserFromToken();
    this.currentUserId = AuthHelper.getUidFromToken();
    this.currentGroupId = AuthHelper.getGroupFromToken();
    this.partnersById$ = this.d3eService.getPartners().pipe(
      map(response => {
        this.partnersById = response.partners.reduce<PartnerMap>((a, e) => {
          a[e.id] = e;
          return a;
        }, {});
        return this.partnersById;
      })
    );
    this.getUserData();
  }

  observeBreakpoint() {
    this.userService.get_organization(authHelper.getGroupFromToken()).pipe(
      switchMap(org => {
        this.currentOrg = org;
        return this.breakpointObserver.observe([
          Breakpoints.XSmall,
          Breakpoints.Small,
          Breakpoints.Medium,
          Breakpoints.Large,
          Breakpoints.XLarge
        ]);
      })
    ).subscribe((state: BreakpointState) => {
      let cols = ['checkbox'];

      if (!this.managing_user || this.isMSP()) {
        cols.push('priority_lock_engaged');
      }

      cols = [...cols, 'company', 'email'];

      if (this.currentOrg.partner_id === null) {
        cols = [...cols, 'partner'];
      }

      if (state.breakpoints[Breakpoints.XSmall] || state.breakpoints[Breakpoints.Small]) {
        this.cardCols = 1;
        cols = [...cols, 'available', 'expiration', 'maint_exp'];
      }

      if (state.breakpoints[Breakpoints.Medium]) {
        this.cardCols = 3;
        cols = [...cols, 'available', 'used', 'pba_used', 'fde_used', 'expiration', 'maint_exp'];
      }

      if (state.breakpoints[Breakpoints.Large] || state.breakpoints[Breakpoints.XLarge]) {
        this.cardCols = 3;
        cols = [...cols, 'subscription', 'active', 'available', 'used', 'pba_used', 'fde_used', 'expiration', 'maint_exp'];
      }

      this.usersColumns = cols;
    });
  }

  isMSP() {
    let isMSP = false;
    if (this.managing_user) {
      isMSP = this.managing_user?.org_type.value === OrgType.MSP;
    }
    return isMSP;
  }

  getExpirationDateFromFilter(filter: ExpirationFilter): Date | null {
    let expirationDate: Date;

    switch (filter) {
      case ExpirationFilter.ALL_SUBSCRIPTIONS:
        expirationDate = null;
        break;
      case ExpirationFilter.LESS_THAN_1_MONTH:
        expirationDate = new Date(Date.now() - (30 * 24 * 60 * 60 * 1000));
        break;
      case ExpirationFilter.LESS_THAN_3_MONTH:
        expirationDate = new Date(Date.now() - (90 * 24 * 60 * 60 * 1000));
        break;
      case ExpirationFilter.LESS_THAN_6_MONTH:
        expirationDate = new Date(Date.now() - (180 * 24 * 60 * 60 * 1000));
        break;
      default:
        expirationDate = null;
        break;
    }
    return expirationDate;
  }

  getUserData() {
    this.loadingUsers$.next(true);
    this.organizationsCount = 0;
    this.subscriptionCount = 0;
    this.usedLicenses = 0;

    this.userService
      .api_get_users_with_subscriptions(this.searchTerm)
      .pipe(
        catchError(() => of([] as IUserSubscriptions[])),
        finalize(() => {
          this.loadingUsers$.next(false);
        }),
        untilDestroyed(this)
      )
      .subscribe(
        users => {
          this.matTableUserDataSource = new MatTableDataSource();
          const managingUser = users.find(u => u.group_id === this.currentGroupId);
          if ( managingUser ) {
            this.setManagingUser(managingUser);
            this.totalLicenses = this.isInfinite(
              managingUser.subscriptions.reduce((c, s) => c + s.count, 0)
            );
          }

          const otherUsers = users.filter(user => user.group_id !== this.currentGroupId);
          this.allOrgsCount = otherUsers.length;
          const unfilteredSubs = otherUsers
            .map(u => u.subscriptions)
            .reduce((a, b) => a.concat(b), []);
          this.allSubsCount = unfilteredSubs.length;
          const totalAllocatedLicenses = unfilteredSubs
            .reduce((a, b) => a + b.allocated + b.count, 0);
          this.allAllocatedLicensesDisplay = this.getLicenseCount(totalAllocatedLicenses);
          this.allUsedLicenses = otherUsers
            .reduce((a, b) => a + b.subscriptions_used + b.subscriptions_used_by_descendants, 0);

          let filteredOtherUsers: IUserSubscriptions[];
          const filterDate = this.getExpirationDateFromFilter(this.expirationFilter);
          if (filterDate) {
            filteredOtherUsers = otherUsers.filter(u => {
              if (!filterDate) {
                return true;
              }
              if (u.subscriptions.length === 0) {
                return true;
              }
              const filteredSubs = u.subscriptions.filter(s => {
                  const subExpired = new Date(s.exp_timestamp);
                  return subExpired >= filterDate;
              });
              u.subscriptions = filteredSubs;
              return !!filteredSubs.length;
            });
          } else {
            filteredOtherUsers = otherUsers;
          }

          if (this.showOnlyActive) {
            filteredOtherUsers = filteredOtherUsers.filter(u => u.active === 1);
          }

          const subscriptions = filteredOtherUsers
              .filter(u => u.subscriptions)
              .map(u => u.subscriptions)
              .reduce((a, b) => a.concat(b), []);

          const allocatedLicenses = subscriptions.reduce((a, b) => a + b.allocated + b.count, 0);
          this.subscriptionCount = subscriptions.length;
          this.allocatedLicensesDisplay = this.getLicenseCount(allocatedLicenses);
          this.usedLicenses = filteredOtherUsers.reduce((a, b) => a + b.subscriptions_used + b.subscriptions_used_by_descendants, 0);

          // We must call this after we get the current user
          this.observeBreakpoint();

          this.organizationsCount = filteredOtherUsers.length;
          this.matTableUserDataSource.data = filteredOtherUsers;
          this.matTableUserDataSource.sortingDataAccessor = (user: IUserSubscriptions,  sortHeaderId: string): any => {
            switch (sortHeaderId) {
              case 'available':
                return this.getAvailableUserLicenses(user);
              case 'allocated':
                return this.getAllocatedUserLicenses(user);
              case 'used':
                return this.getUsedUserLicenses(user);
              case 'subscription':
                return this.getSubscriptionProductTierName(user.tier);
              case 'expiration':
                return new Date(user.last_exp_timestamp);
              case 'active':
                return this.getStatusSort(user);
              case 'partner':
                if (this.partnersById[user.partnerId]) {
                  return this.partnersById[user.partnerId].name;
                }
                return this.fAdoSysDefault;
              default:
                return user[sortHeaderId];
            }
          };
        },
        () => {
          this.notification.showError(this.fUnknownError);
        }
      );
  }

  setManagingUser(managing_user: IUserSubscriptions) {
    this.managing_user = managing_user;
    this.managing_user_total_licenses = this.managing_user.subscriptions_count ? this.managing_user.subscriptions_count : 0;

    let business_id = 0;
    let business_licenses = 0;
    let personal_id = 0;
    let personal_licenses = 0;
    let hardware_id = 0;
    let hardware2_id = 0;
    let hardware_licenses = 0;
    let hardware2_licenses = 0;
    let bitlocker_id = 0;
    let bitlocker_licenses = 0;
    let fde_id = 0;
    let fde_licenses = 0;
    let agent_id = 0;
    let agent_licenses = 0;
    let fallback_id = 0;
    let fallback_licenses = 0;

    managing_user.subscriptions.forEach((sub_detail) => {
      const t_now = new Date(Date.now());
      const t_test = new Date(sub_detail.exp_timestamp);
      if (t_now < t_test) {
        const available = sub_detail.count - sub_detail.used - sub_detail.used_by_descendants;
        if (sub_detail.tier === StripeProductTier.BUSINESS) {
          if (available > business_licenses) {
            business_id = sub_detail.id;
            business_licenses = available;
          } else if ( business_id === 0 ) {
            business_id = sub_detail.id;
          }
        } else if (sub_detail.tier === StripeProductTier.PERSONAL) {
          if (available > personal_licenses) {
            personal_id = sub_detail.id;
            personal_licenses = available;
          } else if ( personal_id === 0 ) {
            personal_id = sub_detail.id;
          }
        } else if (sub_detail.tier === StripeProductTier.HARDWARE) {
          if (available > hardware_licenses) {
            hardware_id = sub_detail.id;
            hardware_licenses = available;
          } else if ( hardware_id === 0 ) {
            hardware_id = sub_detail.id;
          }
        } else if (sub_detail.tier === StripeProductTier.HARDWARE2) {
          if (available > hardware2_licenses) {
            hardware2_id = sub_detail.id;
            hardware2_licenses = available;
          } else if ( hardware2_id === 0 ) {
            hardware2_id = sub_detail.id;
          }
        } else if (sub_detail.tier === StripeProductTier.BITLOCKER) {
          if (available > bitlocker_licenses) {
            bitlocker_id = sub_detail.id;
            bitlocker_licenses = available;
          } else if ( bitlocker_id === 0 ) {
            bitlocker_id = sub_detail.id;
          }
        } else if (sub_detail.tier === StripeProductTier.FDE) {
          if (available > fde_licenses) {
            fde_id = sub_detail.id;
            fde_licenses = available;
          } else if ( fde_id === 0 ) {
            fde_id = sub_detail.id;
          }
        } else if (sub_detail.tier === StripeProductTier.AGENT) {
          if (available > agent_licenses) {
            agent_id = sub_detail.id;
            agent_licenses = available;
          } else if ( agent_id === 0 ) {
            agent_id = sub_detail.id;
          }
        } else {
          // TODO: evaluate fallback removal, appears unused
          if (available > fallback_licenses) {
            fallback_id = sub_detail.id;
            fallback_licenses = available;
          }
        }
      }
    });

    this.managing_user_select_licenses = personal_licenses;
    this.managing_user_plus_licenses = business_licenses;
    this.managing_user_hardware_licenses = hardware_licenses;
    this.managing_user_hardware2_licenses = hardware2_licenses;
    this.managing_user_bitlocker_licenses = bitlocker_licenses;
    this.managing_user_fde_licenses = fde_licenses;
    this.managing_user_agent_licenses = agent_licenses;
    this.managing_user_fallback_licenses = fallback_licenses;
    this.managing_user_select_sub_id = personal_id;
    this.managing_user_plus_sub_id = business_id;
    this.managing_user_hardware_sub_id = hardware_id;
    this.managing_user_hardware2_sub_id = hardware2_id;
    this.managing_user_bitlocker_sub_id = bitlocker_id;
    this.managing_user_fde_sub_id = fde_id;
    this.managing_user_agent_sub_id = agent_id;
    this.managing_user_fallback_sub_id = fallback_id;

    let selectableLicenses = 0;
    if ( this.managing_user_select_licenses > 0) {
       selectableLicenses++;
    }
    if ( this.managing_user_plus_licenses > 0 ) {
      selectableLicenses++;
    }
    if ( this.managing_user_hardware_licenses > 0 ) {
      selectableLicenses++;
    }
    if ( this.managing_user_hardware2_licenses > 0 ) {
      selectableLicenses++;
    }
    if ( this.managing_user_bitlocker_licenses > 0 ) {
      selectableLicenses++;
    }
    this.choosePlan = (selectableLicenses > 1);

  }

  isInfinite(count: number): number | null {
    if (count > this.infiniteLicenses) {
      return null;
    }
  }

  getLicenseCount(actual_lic: number) {
    let str_result: string;
    if (actual_lic >= this.infiniteLicenses) {
      str_result = this.fUnlimited;
    } else {
      str_result = this.i18nNumberFormat(actual_lic);
      str_result = this.padToString(str_result);
    }
    return str_result;
  }

  padToString(stringToPad: number | string): string {
    return stringToPad.toString().padStart(9);
  }

  getLicenseDate(actual_date: Date) {
    let str_result: string;
    if (actual_date >= this.openEndedDate) {
      str_result = this.translate.instant('D3ED_NEVER');
    } else if (actual_date < this.tomorrow) {
      str_result = this.fExpired;
    } else {
      str_result = actual_date.toDateString();
    }
    return str_result;
  }

  disallowAddOrganizations() {
    return this.loadingUsers$.value || this.managing_user?.subscriptions_count === 0;
  }

  getAllAvailableLicenses() {
    return this.getLicenseCount(this.managing_user ?
        (this.managing_user.subscriptions_count ? this.managing_user.subscriptions_count : 0) : 0);
  }

  getAvailableUserLicenses(user: IUserSubscriptions) {
    return this.getLicenseCount( user ? (user.subscriptions_count ? user.subscriptions_count : 0) : 0);
  }

  getAvailableSubscriptionLicenses(sub_detail: IUserSubscriptionDetail) {
    return this.getLicenseCount( sub_detail ? (sub_detail.count ? sub_detail.count : 0) : 0);
  }

  getTotalSubscriptionLicenses(sub_detail: IUserSubscriptionDetail) {
    return this.getLicenseCount( sub_detail ? (sub_detail.count + sub_detail.allocated ? sub_detail.count + sub_detail.allocated : 0) : 0);
  }

  getAllocatedUserLicenses(user: IUserSubscriptions) {
    return this.getLicenseCount(user ? (user.subscriptions_allocated ? user.subscriptions_allocated : 0) : 0);
  }

  getAllocatedSubscriptionLicenses(sub_detail: IUserSubscriptionDetail) {
    return this.getLicenseCount(sub_detail ? (sub_detail.allocated ? sub_detail.allocated : 0) : 0);
  }

  getUsedUserLicenses(user: IUserSubscriptions) {
    const used = user ? (user.subscriptions_used ? user.subscriptions_used : 0) : 0;
    const used_by_descendants = user ? (user.subscriptions_used_by_descendants ? user.subscriptions_used_by_descendants : 0) : 0;
    return this.getLicenseCount(used + used_by_descendants);
  }

  getUsedSubscriptionLicenses(sub_detail: IUserSubscriptionDetail) {
    const used = sub_detail ? (sub_detail.used ? sub_detail.used : 0) : 0;
    const used_by_descendants = sub_detail ? (sub_detail.used_by_descendants ? sub_detail.used_by_descendants : 0) : 0;
    const total = this.i18nNumberFormat(used + used_by_descendants);
    return this.padToString(total);
  }

  getPbaUsedLicenses(sub_detail: IUserSubscriptionDetail) {
    return this.padToString(sub_detail?.pba_used ? sub_detail.pba_used : 0);
  }

  getFdeUsedLicenses(sub_detail: IUserSubscriptionDetail) {
    return this.padToString(sub_detail?.fde_used ? sub_detail.fde_used : 0);
  }

  getAgentUsedLicenses(sub_detail: IUserSubscriptionDetail) {
    return this.padToString(sub_detail?.agent_used ? sub_detail.agent_used : 0);
  }

  getSubscriptionExpiration(sub_detail: IUserSubscriptionDetail) {
    const expire = new Date(sub_detail.exp_timestamp);
    return this.getLicenseDate(expire);
  }

  getSubscriptionProductTierName(tier: number) {
    if (tier) {
      if (tier === StripeProductTier.BUSINESS) {
        return this.fD3EPremium;
      }
      if (tier === StripeProductTier.PERSONAL) {
        return this.fD3EPersonal;
      }
      if ( tier === StripeProductTier.HARDWARE ) {
        return this.translate.instant('product_and_plan.plan.hardware-label');
      }
      if ( tier === StripeProductTier.HARDWARE2 ) {
        return this.translate.instant('product_and_plan.plan.hardware2-label');
      }
      if ( tier === StripeProductTier.BITLOCKER ) {
        return this.translate.instant('product_and_plan.plan.bitlocker-label');
      }
      if ( tier === StripeProductTier.FDE ) {
        return this.translate.instant('product_and_plan.plan.fde-label');
      }
      if ( tier === StripeProductTier.AGENT ) {
        return this.translate.instant('product_and_plan.plan.agent-label');
      }
    }
    return this.fD3EFree;
  }

  i18nNumberFormat(value: number): string {
      const language = localStorage.getItem('language') ?? 'en-US';
      return isFinite(value) ? new Intl.NumberFormat(language).format(value) : '0';
  }

  addOrganization(): void {
    forkJoin({
      managingOrg: this.userService.get_organization(this.managing_user.group_id),
      policies: this.d3eService.getD3EPolicies()  // fetch policy templates
    }).pipe(
        // Open the dialog box
        switchMap(({managingOrg, policies}) => {
          let edition = PlanChoiceValue.PLUS;
          if ( this.managing_user_plus_licenses > 0 ) {
            edition = PlanChoiceValue.PLUS;
          } else if ( this.managing_user_select_licenses > 0 ) {
            edition = PlanChoiceValue.SELECT;
          } else if ( this.managing_user_hardware_licenses > 0 ) {
            edition = PlanChoiceValue.HARDWARE;
          } else if ( this.managing_user_bitlocker_licenses > 0 ) {
            edition = PlanChoiceValue.BITLOCKER;
          } else if ( this.managing_user_hardware2_licenses > 0 ) {
            edition = PlanChoiceValue.HARDWARE2;
          } else if ( this.managing_user_fde_licenses > 0 ) {
            edition = PlanChoiceValue.FDE;
          }

          const dialogRef = this.dialog.open(AddOrganizationComponent, {
            width: '675px',
            height: 'auto',
            disableClose: true,
            data: {
              availableLicenses: this.getAllAvailableLicenses(),
              policies: policies,
              maxDate: this.openEndedDate,
              minDate: this.yesterday,
              defaultDate: this.defaultDate,
              maxDurationDays: this.maxDurationDays,
              choosePlan: this.choosePlan,
              edition: edition,
              plusCount: this.managing_user_plus_licenses,
              selectCount: this.managing_user_select_licenses,
              hardwareCount: this.managing_user_hardware_licenses,
              hardware2Count: this.managing_user_hardware2_licenses,
              bitLockerCount: this.managing_user_bitlocker_licenses,
              fdeCount: this.managing_user_fde_licenses,
              agentCount: this.managing_user_agent_licenses,
              enableEndpointMap: managingOrg.enable_endpoint_map,
              enablePBAExtendedFeatures: managingOrg.default_pba_extended_features,
              enablePBALogErase: managingOrg.default_pba_log_erase
            }
          });
          return dialogRef.afterClosed();
        }),

        // After dialog is closed, make call to create user
        switchMap(newUserPostData => {

          // newUserPostData comes from the dialog box.  If it's falsy, then the dialog was cancelled.
          if (newUserPostData) {
            const is_reseller = this.managing_user && this.managing_user.org_type.value === OrgType.RESELLER;
            let defaultPolicyId = -1;
            if ( newUserPostData.defaultPolicy !== undefined ) {
              defaultPolicyId = newUserPostData.defaultPolicy;
            }
            const userInfo: ICreateUser = {
              company: newUserPostData.company,
              username: newUserPostData.email,
              email: newUserPostData.email,
              reserve_domain: newUserPostData.reserve_domain,
              email_notify_language: newUserPostData.email_notify_language,
              firstName: newUserPostData.fname,
              lastName: newUserPostData.lname,
              postal_code: newUserPostData.zip,
              default_policy_id: defaultPolicyId,
              country_code: '',
              role: UserRole.ADMIN,
              parent_group_id: this.managing_user.group_id,
              password: newUserPostData.password,
              business_edition: (is_reseller
                  || newUserPostData.edition === PlanChoiceValue.PLUS
                  || newUserPostData.edition === PlanChoiceValue.BITLOCKER
                  || newUserPostData.edition === PlanChoiceValue.FDE
                  || newUserPostData.edition === PlanChoiceValue.AGENT) ? 1 : 0,
              send_email: newUserPostData.sendEmail,
              partnerId: newUserPostData.partnerId,
              org_type: is_reseller ? OrgType.MSP : OrgType.CUSTOMER,
              generateKeys: newUserPostData.generateKeys,
              keyPassword: newUserPostData.keyPassword,
              allow_maps: newUserPostData.enableEndpointMap,
              enable_pba_extended_features: newUserPostData.enablePBAExtendedFeatures,
              enable_pba_log_erase: newUserPostData.enablePBALogErase,
            };

            // The next switchMap will need the newUserPostData from the dialog output and the
            // response object from the post user call.
            return forkJoin([of(newUserPostData), this.userService.api_post_organization(userInfo)]);
          } else {
            return EMPTY;
          }
        }),

        // Allocate a subscription
        // The values below that are the arguments come from the forkJoin above
        switchMap(([newUserPostData, createdUser]) => {
          let sub_id = this.managing_user_select_sub_id;  // default
          let expDate = newUserPostData.expirationDate;
          if ( newUserPostData.edition === PlanChoiceValue.PLUS ) {
            sub_id = this.managing_user_plus_sub_id;
          } else if ( newUserPostData.edition === PlanChoiceValue.SELECT ) {
            sub_id = this.managing_user_select_sub_id;
          } else if ( newUserPostData.edition === PlanChoiceValue.HARDWARE ) {
            expDate = this.openEndedDate;
            sub_id = this.managing_user_hardware_sub_id;
          } else if ( newUserPostData.edition === PlanChoiceValue.HARDWARE2 ) {
            expDate = this.openEndedDate;
            sub_id = this.managing_user_hardware2_sub_id;
          } else if ( newUserPostData.edition === PlanChoiceValue.BITLOCKER ) {
            sub_id = this.managing_user_bitlocker_sub_id;
          } else if ( newUserPostData.edition === PlanChoiceValue.FDE ) {
            sub_id = this.managing_user_fde_sub_id;
          }else if ( newUserPostData.edition === PlanChoiceValue.AGENT ) {
            sub_id = this.managing_user_agent_sub_id;
          }
          return this.d3eService.subscription_allocate({
            source_id: sub_id,
            target_id: null,
            count: +newUserPostData.licenses,
            target_group_id: createdUser.group_id,
            subscription_start: null,
            subscription_end: expDate,
            maintenance_end: newUserPostData.maintenance_end,
            max_duration_days: newUserPostData.maxDurationDays,
            notify: false,
            notifyLanguage: null,
            sub_type: newUserPostData.subType,
            enablePBA_Features: newUserPostData.enablePBAExtendedFeatures,
            enablePBALogErasure: newUserPostData.enablePBALogErase
          });
        }),
        finalize(() => {
          this.store.dispatch(loadOrganizations());
          this.getUserData();
          this.loadingUsers$.next(false);
        })
      ).subscribe(
      () => {
          this.loadingUsers$.next(true);
          this.notification.showSuccess(this.fNewOrgCreated);
        },
      () => {
          this.notification.showError(this.fUnknownError);
        }
      );
  }

  addSubscriptionButton(): void {
    const target_user_id = +Object.keys(this.selected)[0];
    let group_id;
    let partnerId = -1;

    let source_sub_count = 0;
    // If a reseller modifying an MSP, can choose, else must match whatever subscription that Org already has,
    // this is just setting the default value to match
    let edition_choice = PlanChoiceValue.PLUS;
    let max_count = this.managing_user_plus_licenses;

    if ( this.managing_user_plus_licenses > 0 ) {
      edition_choice = PlanChoiceValue.PLUS;
      max_count = this.managing_user_plus_licenses;
    } else if ( this.managing_user_select_licenses > 0 ) {
      edition_choice = PlanChoiceValue.SELECT;
      max_count = this.managing_user_select_licenses;
    } else if ( this.managing_user_hardware_licenses > 0 ) {
      edition_choice = PlanChoiceValue.HARDWARE;
      max_count = this.managing_user_hardware_licenses;
    } else if ( this.managing_user_hardware2_licenses > 0 ) {
      edition_choice = PlanChoiceValue.HARDWARE2;
      max_count = this.managing_user_hardware2_licenses;
    } else if ( this.managing_user_bitlocker_licenses > 0 ) {
      edition_choice = PlanChoiceValue.BITLOCKER;
      max_count = this.managing_user_bitlocker_licenses;
    } else if ( this.managing_user_fde_licenses > 0 ) {
      edition_choice = PlanChoiceValue.FDE;
      max_count = this.managing_user_fde_licenses;
    }

    this.matTableUserDataSource.data.forEach((user) => {
      if (user.id === target_user_id) {
        group_id = user.group_id;
        partnerId = user.partnerId ?? -1;
        // Don't care if expired or all used, just want to match type with the highest count
        user.subscriptions.forEach((sub_detail) => {
          if (sub_detail.tier === edition_choice && sub_detail.count > source_sub_count) {
            source_sub_count = sub_detail.count;
          }
        });
      }
    });
    const dialogRef = this.dialog.open(AddChildSubscriptionComponent, {
      width: '625px',
      height: 'auto',
      disableClose: true,
      data: {
        availableLicenses: this.getAllAvailableLicenses(),
        maxDate: this.openEndedDate,
        minDate: this.yesterday,
        expirationDate: this.defaultDate,
        maxDurationDays: null,
        choosePlan: this.choosePlan,
        edition: edition_choice,
        maxCount: max_count,
        plusCount: this.managing_user_plus_licenses,
        selectCount: this.managing_user_select_licenses,
        hardwareCount: this.managing_user_hardware_licenses,
        hardware2Count: this.managing_user_hardware2_licenses,
        bitLockerCount: this.managing_user_bitlocker_licenses,
        fdeCount: this.managing_user_fde_licenses,
        agentCount: this.managing_user_agent_licenses,
        enablePBAExtendedFeatures: partnerId !== Partners.Digistor,
        enablePBALogErase: partnerId === Partners.Digistor
      }
    });
    dialogRef.afterClosed().subscribe(addChildSubData => {
      if (addChildSubData) {
        let managing_sub_id: number;
        let expDate = addChildSubData.expirationDate;
        if (addChildSubData.edition === PlanChoiceValue.PLUS) {
          managing_sub_id = this.managing_user_plus_sub_id;
        } else if (addChildSubData.edition === PlanChoiceValue.SELECT) {
          managing_sub_id = this.managing_user_select_sub_id;
        } else if (addChildSubData.edition === PlanChoiceValue.HARDWARE) {
          expDate = this.openEndedDate;
          managing_sub_id = this.managing_user_hardware_sub_id;
        } else if (addChildSubData.edition === PlanChoiceValue.HARDWARE2) {
          expDate = this.openEndedDate;
          managing_sub_id = this.managing_user_hardware2_sub_id;
        } else if (addChildSubData.edition === PlanChoiceValue.BITLOCKER) {
          managing_sub_id = this.managing_user_bitlocker_sub_id;
        } else if (addChildSubData.edition === PlanChoiceValue.FDE) {
          managing_sub_id = this.managing_user_fde_sub_id;
        }  else if (addChildSubData.edition === PlanChoiceValue.AGENT) {
          managing_sub_id = this.managing_user_agent_sub_id;
        } else {
          managing_sub_id = this.managing_user_fallback_sub_id;
        }

        this.d3eService.subscription_allocate({
          source_id: managing_sub_id,
          target_id: null,
          count: addChildSubData.licenses,
          target_group_id: group_id,
          subscription_start: null,
          subscription_end: expDate,
          maintenance_end: addChildSubData.maintenance_end,
          max_duration_days: addChildSubData.maxDurationDays,
          notify: addChildSubData.sendEmail,
          notifyLanguage: addChildSubData.language,
          sub_type: addChildSubData.subType,
          enablePBA_Features: addChildSubData.enablePBAExtendedFeatures,
          enablePBALogErasure: addChildSubData.enablePBALogErase,
        }).pipe(
          finalize(() => {
            this.getUserData();
            this.store.dispatch(loadOrganizations());
          })
        )
        .subscribe(
          () => {
            this.notification.showSuccess(this.fOrgUpdated);
          },
          () => {
            this.notification.showError(this.fUnknownError);
          }
        );
      }
    });
  }

  editOrg(user: IUser) {
    forkJoin({
      managingGroup: this.userService.get_organization(this.managing_user.group_id),
      group: this.userService.get_organization(user.group_id)
    }).pipe(
      switchMap(({managingGroup, group}) => this.dialog.open(EditOrgComponent, {
          width: '400px',
          data: {
            user: user,
            choosePlan: this.choosePlan,
            managingGroup: managingGroup,
            group: group,
          },
        }).afterClosed()
      ),
      untilDestroyed(this)
    ).subscribe(newUser => {
      if (newUser) {
        this.loadingUsers$.next(true);
        this.userService.api_edit_org(newUser)
          .pipe(finalize(() => this.getUserData()))
          .subscribe(
            () => {
              this.notification.showSuccess(this.fOrgUpdated);
            },
            () => {
              this.notification.showError(this.fUnknownError);
            }
        );
      }
    });
  }

  editSubLicenseCount(sub_detail: IUserSubscriptionDetail) {
    let maxCount: number;
    let managing_sub_id = this.managing_user_fallback_sub_id;

    if (sub_detail.tier === StripeProductTier.BUSINESS) {
      managing_sub_id = this.managing_user_plus_sub_id;
      maxCount = this.managing_user_plus_licenses + sub_detail.count;
    } else if (sub_detail.tier === StripeProductTier.PERSONAL) {
      managing_sub_id = this.managing_user_select_sub_id;
      maxCount = this.managing_user_select_licenses + sub_detail.count;
    } else if (sub_detail.tier === StripeProductTier.HARDWARE) {
      managing_sub_id = this.managing_user_hardware_sub_id;
      maxCount = this.managing_user_hardware_licenses + sub_detail.count;
    } else if (sub_detail.tier === StripeProductTier.HARDWARE2) {
      managing_sub_id = this.managing_user_hardware2_sub_id;
      maxCount = this.managing_user_hardware2_licenses + sub_detail.count;
    } else if (sub_detail.tier === StripeProductTier.BITLOCKER) {
      managing_sub_id = this.managing_user_bitlocker_sub_id;
      maxCount = this.managing_user_bitlocker_licenses + sub_detail.count;
    } else if (sub_detail.tier === StripeProductTier.FDE) {
      managing_sub_id = this.managing_user_fde_sub_id;
      maxCount = this.managing_user_fde_licenses + sub_detail.count;
    } else if (sub_detail.tier === StripeProductTier.AGENT) {
      managing_sub_id = this.managing_user_agent_sub_id;
      maxCount = this.managing_user_agent_licenses + sub_detail.count;
    } else {
      managing_sub_id = this.managing_user_fallback_sub_id;
      maxCount = this.managing_user_fallback_licenses + sub_detail.count;
    }

    const dialogRef = this.dialog.open(EditLicenseCountComponent, {
      width: '525px',
      data: {
        subType: sub_detail.type,
        total: sub_detail.count + sub_detail.allocated,
        count: sub_detail.count,
        allocated: sub_detail.allocated,
        used: sub_detail.used + sub_detail.used_by_descendants,
        available: this.managing_user.subscriptions_count,  // TODO Fix managing_user.subscriptions_count
        infiniteLicenses: this.infiniteLicenses,
        maxCount: maxCount,
        enablePBAExtendedFeatures: sub_detail.enable_pba_extended_features,
        enablePBALogErase: sub_detail.enable_pba_log_erase
      },
    });
    dialogRef.afterClosed().subscribe(result => {
        const { newCount, notify, notifyLanguage, selectedSubType, enablePBAExtendedFeatures, enablePBALogErase } = Object.assign({newCount: null}, result);

        if (newCount !== null) {
          this.d3eService.subscription_allocate(
            {
              source_id: managing_sub_id,
              target_id: sub_detail.id,
              count: newCount - sub_detail.count,
              target_group_id: null,
              subscription_start: null,
              subscription_end: null,
              maintenance_end: null,
              max_duration_days: null,
              notify: notify,
              notifyLanguage: notifyLanguage,
              sub_type: selectedSubType,
              enablePBA_Features: enablePBAExtendedFeatures,
              enablePBALogErasure: enablePBALogErase,
            })
                .pipe(finalize(() => this.getUserData()))
                .subscribe(
                  () => {
                    this.notification.showSuccess(this.fLicenseCountUpdated);
                  },
                  () => {
                    this.notification.showError(this.fUnknownError);
                  }
          );
        }
    });
  }

  editSubExpDate(sub_detail: IUserSubscriptionDetail) {
    // delay duration is days, but UI is trying to be more friendly, and be months
    const dialogRef = this.dialog.open(EditExpDateComponent, {
      width: '525px',
      data: {
        selectedEdition: sub_detail.tier,
        date: new Date(sub_detail.exp_timestamp),
        maintDate: new Date(sub_detail.maint_exp_timestamp),
        delay: sub_detail.max_duration_days,
        minDate: this.lastMonth,  // Allow it to be completely expired
        maxDate: this.openEndedDate,
        enableDelay: !(sub_detail.used > 0), // Hide delay if a device is activated,
        subType: sub_detail.type,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const { date, delay, notify, notifyLanguage, selectedSubType, enablePBAExtendedFeatures, enablePBALogErase, maintDate } = result;
        let expDate = date;
        if (sub_detail.tier === StripeProductTier.HARDWARE) {
          expDate = this.openEndedDate;
        } else if (sub_detail.tier === StripeProductTier.HARDWARE2) {
          expDate = this.openEndedDate;
        }
        if (date) {
          this.d3eService.subscription_allocate({
              source_id: sub_detail.id,
              target_id: sub_detail.id,
              count: 0,
              target_group_id: null,
              subscription_start: null,
              subscription_end: expDate,
              maintenance_end: maintDate,
              max_duration_days: delay,
              notify: notify,
              notifyLanguage: notifyLanguage,
              sub_type: selectedSubType,
              enablePBA_Features: enablePBAExtendedFeatures,
              enablePBALogErasure: enablePBALogErase,
            }).pipe(finalize(() => this.getUserData()))
            .subscribe(
              () => {
                this.notification.showSuccess(this.fExpirationUpdated);
              },
              () => {
                this.notification.showError(this.fUnknownError);
              }
          );
        }
      }
    });
  }

  deactivateOrganization(): void {
    const title = this.fDeactivateOrgsTitle;
    const content = this.fDeactivateOrgsContent;

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title,
        content,
        rejectBtnName: this.fCancel,
        acceptBtnName: this.fConfirm,
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userService.deactivate_organization(this.selected)
            .pipe(
                finalize(() => this.getUserData())
            )
            .subscribe(
              () => {
                this.notification.showSuccess(this.fOrgsDeactivated);
                this.selected = {};
                this.disableReactivateAndDeactivateBtns();
              },
              () => {
                this.notification.showError(this.fUnknownError);
            });
      } else {
        // Button stays in focus for some reason without this?
        document.getElementById('buttonDeactivateOrgs').blur();
      }
    });
  }

  reactivateOrganization(): void {
    const title = this.fReactivateOrgsTitle;
    const content = this.fReactivateOrgsContent;
    const additionalContent = [this.fReactivateOrgsContentAdd];
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title,
        content,
        additionalContent,
        rejectBtnName: this.fCancel,
        acceptBtnName: this.fConfirm,
        showRejectBtn: true,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.userService.reactivate_organization(this.selected)
            .pipe(
                finalize(() => this.getUserData())
            )
            .subscribe(
              () => {
                this.notification.showSuccess(this.fOrgsReactivated);
                this.selected = {};
                this.disableReactivateAndDeactivateBtns();
              },
              () => {
                this.notification.showError(this.fUnknownError);
              });
      } else {
        // Button stays in focus for some reason without this?
        document.getElementById('buttonReactivateOrgs').blur();
      }
    });
  }

  onCheckChanged(event, user) {
    if (event.checked) {
      this.selected[user.id] = user.group_id;
    } else {
      delete this.selected[user.id];
    }
    this.active_orgs_selected = this.matTableUserDataSource.data.reduce((acc, org) =>
        org.active === 1 && org.id in this.selected ? ++acc : acc, 0
    );
    this.deactivated_orgs_selected = this.matTableUserDataSource.data.reduce((acc, org) =>
        org.active === 0 && org.id in this.selected ? ++acc : acc, 0
    );
    this.priority_lock_engaged_selected = this.matTableUserDataSource.data.reduce((acc, org) =>
        org.priority_lock_engaged && org.id in this.selected ? ++acc : acc, 0
    );
    this.priority_lock_released_selected = this.matTableUserDataSource.data.reduce((acc, org) =>
        org.priority_lock_engaged === false && org.id in this.selected ? ++acc : acc, 0
    );

    this.disableReactivateAndDeactivateBtns();
    const length = Object.keys(this.selected).length;
    this.disable_add_subscription_btn = length !== 1;
  }

  isChecked(user) {
    return user.id in this.selected;
  }

  disableReactivateAndDeactivateBtns () {
    const disallowOrgBtns = this.disallowAddOrganizations();
    if (disallowOrgBtns) {
      this.disable_reactivate_btn = true;
      this.disable_deactivate_btn = true;
      this.disable_priority_lock_btn = true;
      this.disable_priority_lock_release_btn = true;
    } else {
      const length = Object.keys(this.selected).length;
      if (length !== 0) {
        this.disable_reactivate_btn = this.deactivated_orgs_selected === 0;
        this.disable_deactivate_btn = this.active_orgs_selected === 0;
        this.disable_priority_lock_btn = this.priority_lock_released_selected === 0;
        this.disable_priority_lock_release_btn = this.priority_lock_engaged_selected === 0;
      } else {
        this.disable_reactivate_btn = true;
        this.disable_deactivate_btn = true;
        this.disable_priority_lock_btn = true;
        this.disable_priority_lock_release_btn = true;
      }
    }
  }

  searchOrganizations() {
    this.expirationFilter = ExpirationFilter.ALL_SUBSCRIPTIONS;
    this.getUserData();
  }

  clearSearch() {
    this.searchTerm = '';
    this.onExpirationSelectChanged(ExpirationFilter.LESS_THAN_1_MONTH);
  }

  onExpirationSelectChanged(expirationFilter: ExpirationFilter) {
    this.expirationFilter = expirationFilter;
    this.getUserData();
  }

  engagePriorityLockSelectedOrganizations() {
    const title = this.translate.instant('priority_lock.lock_button');
    const content = this.translate.instant( 'priority_lock.lock_content');

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title,
        content,
        rejectBtnName: this.fCancel,
        acceptBtnName: this.fConfirm,
        showRejectBtn: true,
      },
    });
      const selected_groups = [];
      for (const [key, val] of Object.entries(this.selected)) {
        selected_groups.push(val);
      }
      dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.d3eService.applyPriorityLock(selected_groups, true)
            .pipe(
                finalize(() => this.getUserData())
            )
            .subscribe(
              () => {
                this.notification.showSuccess(this.translate.instant('priority_lock.lock_success'));
                this.selected = {};
                this.disableReactivateAndDeactivateBtns();
              },
              error => {
                this.notification.showError(this.fUnknownError);
            });
      } else {
        // Button stays in focus for some reason without this?
        document.getElementById('buttonPriorityEngageOrgs').blur();
      }
    });

  }

  releasePriorityLockSelectedOrganizations() {
    const title = this.translate.instant('priority_lock.release_button');
    const content = this.translate.instant( 'priority_lock.release_content');

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title,
        content,
        rejectBtnName: this.fCancel,
        acceptBtnName: this.fConfirm,
        showRejectBtn: true,
      },
    });
    const selected_groups = [];
    for (const [key, val] of Object.entries(this.selected)) {
      selected_groups.push(val);
    }
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.d3eService.applyPriorityLock(selected_groups, false)
            .pipe(
                finalize(() => this.getUserData())
            )
            .subscribe(
              () => {
                this.notification.showSuccess(this.translate.instant('priority_lock.release_success'));
                this.selected = {};
                this.disableReactivateAndDeactivateBtns();
              },
              error => {
                this.notification.showError(this.fUnknownError);
            });
      } else {
        // Button stays in focus for some reason without this?
        document.getElementById('buttonPriorityReleaseOrgs').blur();
      }
    });
  }

  onFilterInactive() {
    this.getUserData();
  }

  private getStatusSort(user) {
     if (! user.active) {
       return 0;
     }
     const subscriptions: IUserSubscriptionDetail[] = user.subscriptions;
     if ( subscriptions.find( sub => sub.expired ) ) {
       return 1;
     }
     if ( subscriptions.find( sub => sub.type === this.subtype.LICENSE_TYPE_FREE ) ) {
       return 2;
     }
     if ( subscriptions.find( sub => sub.type === this.subtype.LICENSE_TYPE_TRIAL ) ) {
       return 3;
     }
     if ( subscriptions.find( sub => sub.type === this.subtype.LICENSE_TYPE_TIME_LIMITED ) ) {
       return 4;
     }
     return 5;

  }
}
